import { useEffect, useState } from 'react';

/**
 * The pixel box a full-screen crop modal's picture may fill — re-measured when
 * the window resizes or a phone rotates.
 *
 * Crop surfaces size their image from this instead of a fixed `max-h-[60vh]`:
 * the whole photo stays in view with nothing to scroll, and it is as large as
 * the screen allows. `null` until the stage has been laid out.
 */
export function useFitBox(ref, active = true) {
    const [box, setBox] = useState(null);

    useEffect(() => {
        if (!active) return undefined;
        const node = ref.current;
        if (!node) return undefined;
        const measure = () => {
            const { width, height } = node.getBoundingClientRect();
            const next = { width: Math.floor(width), height: Math.floor(height) };
            setBox((prev) => (prev && prev.width === next.width && prev.height === next.height ? prev : next));
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, [ref, active]);

    return box;
}

/** The page behind a full-screen modal must not scroll along with a drag. */
export function useBodyScrollLock(active = true) {
    useEffect(() => {
        if (!active) return undefined;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previous; };
    }, [active]);
}

/**
 * Inline size for a crop surface's `<img>` so it fits `box` without distortion:
 * both dimensions capped, both left to `auto` so the aspect ratio holds.
 */
export function fitImageStyle(box) {
    if (!box?.width || !box?.height) return { maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto' };
    return { maxWidth: `${box.width}px`, maxHeight: `${box.height}px`, width: 'auto', height: 'auto' };
}
